import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DialogueLine, EntryTake } from '../../types';
import { getLineStatus } from '../common/LineDots';
import { Mic, Volume2, Play, Pause, Layers, Lock } from 'lucide-react';

export interface DialogueTimelineProps {
  duration: number; // total duration in seconds
  currentTime: number; // current playhead time in seconds
  dialogueLines: DialogueLine[];
  takes: EntryTake[];
  currentPerformer?: string;
  selectedLineId: string | null;
  selectedLineIds?: Set<string>;
  auditioningTakeId?: string | null;
  onSelectLine: (lineId: string, layer?: 'user' | 'orig') => void;
  onToggleSelectLine?: (lineId: string, layer?: 'user' | 'orig') => void;
  onUpdateLineTiming: (lineId: string, start: number, end: number) => void;
  onUpdateTakeTiming?: (takeId: string, start: number, end: number) => void;
  onSeek: (time: number) => void;
  onAuditionSegment?: (start: number, end: number) => void;
  onAuditionTake?: (take: EntryTake) => void;
  onNudgeTakeSync?: (takeId: string, deltaSec: number) => void;
  isPlaying?: boolean;
}

export const DialogueTimeline: React.FC<DialogueTimelineProps> = ({
  duration,
  currentTime,
  dialogueLines,
  takes,
  currentPerformer,
  selectedLineId,
  selectedLineIds,
  auditioningTakeId = null,
  onSelectLine,
  onToggleSelectLine,
  onUpdateLineTiming,
  onUpdateTakeTiming,
  onSeek,
  onAuditionSegment,
  onAuditionTake,
  onNudgeTakeSync,
  isPlaying = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingHandle, setDraggingHandle] = useState<{
    lineId?: string;
    takeId?: string;
    type: 'start' | 'end';
  } | null>(null);

  const safeDuration = Math.max(0.1, duration);

  // Time conversion helper
  const xToTime = useCallback((clientX: number, allowNegative: boolean = false) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    // Offset for track header width (110px)
    const trackWidth = rect.width - 110;
    if (trackWidth <= 0) return 0;
    const relativeX = clientX - rect.left - 110;
    const minX = allowNegative ? -trackWidth * 0.5 : 0;
    const clampedX = Math.max(minX, Math.min(relativeX, trackWidth));
    const ratio = clampedX / trackWidth;
    const rawTime = ratio * safeDuration;
    return Math.round(rawTime * 20) / 20; // 50ms snap
  }, [safeDuration]);

  // Drag interaction
  useEffect(() => {
    if (!draggingHandle) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newTime = xToTime(e.clientX);

      if (draggingHandle.takeId) {
        const activeTake = takes.find((t) => t.id === draggingHandle.takeId);
        if (!activeTake || !onUpdateTakeTiming || activeTake.isLocked || activeTake.isPartnerTake) return;
        const currentStart = activeTake.lineStart ?? 0;
        const currentEnd = activeTake.lineEnd ?? safeDuration;

        if (draggingHandle.type === 'start') {
          const newTime = xToTime(e.clientX, true);
          const clampedStart = Math.min(newTime, currentEnd - 0.1);
          onUpdateTakeTiming(draggingHandle.takeId, clampedStart, currentEnd);
        } else {
          const newTime = xToTime(e.clientX, false);
          const clampedEnd = Math.min(safeDuration, Math.max(newTime, currentStart + 0.1));
          onUpdateTakeTiming(draggingHandle.takeId, currentStart, clampedEnd);
        }
      } else if (draggingHandle.lineId) {
        const activeLine = dialogueLines.find((l) => l.id === draggingHandle.lineId);
        if (!activeLine) return;

        const currentStart = activeLine.start ?? activeLine.startTime ?? 0;
        const currentEnd = activeLine.end ?? activeLine.endTime ?? safeDuration;

        if (draggingHandle.type === 'start') {
          const newTime = xToTime(e.clientX, false);
          const clampedStart = Math.max(0, Math.min(newTime, currentEnd - 0.2));
          onUpdateLineTiming(draggingHandle.lineId, clampedStart, currentEnd);
        } else {
          const newTime = xToTime(e.clientX, false);
          const clampedEnd = Math.min(safeDuration, Math.max(newTime, currentStart + 0.2));
          onUpdateLineTiming(draggingHandle.lineId, currentStart, clampedEnd);
        }
      }
    };

    const handleMouseUp = () => {
      setDraggingHandle(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingHandle, dialogueLines, takes, safeDuration, xToTime, onUpdateLineTiming, onUpdateTakeTiming]);

  // Click on background to seek
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingHandle) return;
    const target = e.target as HTMLElement;
    if (target.dataset.role === 'handle') return;

    const seekTime = Math.max(0, xToTime(e.clientX, false));
    onSeek(seekTime);
  };

  const playheadPercent = Math.min(100, Math.max(0, (currentTime / safeDuration) * 100));

  const selectedLine = dialogueLines.find((l) => l.id === selectedLineId);
  const selectedStart = selectedLine ? (selectedLine.start ?? selectedLine.startTime ?? 0) : 0;
  const selectedEnd = selectedLine ? (selectedLine.end ?? selectedLine.endTime ?? safeDuration) : safeDuration;

  // Build second marker points for time ruler
  const rulerTicksCount = Math.min(30, Math.ceil(safeDuration));
  const tickInterval = safeDuration > 30 ? 5 : safeDuration > 15 ? 2 : 1;
  const ticks = [];
  for (let s = 0; s <= safeDuration; s += tickInterval) {
    ticks.push(s);
  }

  return (
    <div className="ss-timeline" style={{ background: '#0a0d1e', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-glass)', overflow: 'hidden' }}>
      {/* Header bar: Selected Line details & audition */}
      <div className="ss-timeline-header" style={{ padding: '0.65rem 1rem', background: 'rgba(255, 255, 255, 0.03)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600, color: '#f8fafc', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Layers size={16} color="var(--accent-purple)" />
            SoulStage
          </span>
          {selectedLine && (
            <span style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', padding: '2px 8px', borderRadius: '4px', fontFamily: 'var(--font-mono)', fontSize: '11px', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              {selectedStart.toFixed(1)}s – {selectedEnd.toFixed(1)}s ({(selectedEnd - selectedStart).toFixed(1)}s)
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {selectedLine && onAuditionSegment && (
            <button
              type="button"
              onClick={() => onAuditionSegment(selectedStart, selectedEnd)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(139, 92, 246, 0.2)', color: '#e9d5ff', padding: '3px 10px', borderRadius: '6px', fontSize: '11px', border: '1px solid rgba(139, 92, 246, 0.4)', fontWeight: 600, cursor: 'pointer' }}
              title="Audition original dialogue line timestamp"
            >
              <Volume2 size={12} />
              Audition Line
            </button>
          )}
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: '11px' }}>
            {currentTime.toFixed(1)}s / {safeDuration.toFixed(1)}s
          </span>
        </div>
      </div>

      {/* Main Multi-Layer Tracks Container */}
      <div
        ref={containerRef}
        onClick={handleTimelineClick}
        style={{ position: 'relative', userSelect: 'none', cursor: 'pointer' }}
      >
        {/* Unified Scrubbable Playhead slicing across both tracks */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `calc(110px + (100% - 110px) * ${playheadPercent / 100})`,
            width: '2px',
            background: '#ef4444',
            boxShadow: '0 0 12px rgba(239, 68, 68, 0.9)',
            zIndex: 40,
            pointerEvents: 'none',
            transform: 'translateX(-50%)'
          }}
        >
          {/* Top Diamond Flag */}
          <div
            style={{
              position: 'absolute',
              top: '0px',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: '10px',
              height: '10px',
              background: '#ef4444',
              borderRadius: '2px',
              boxShadow: '0 0 8px rgba(239, 68, 68, 0.9)'
            }}
          />
        </div>

        {/* ── LAYER 1 (TOP TRACK): USER'S VOICE / TAKES ── */}
        <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          {/* Track Header Label */}
          <div
            style={{
              width: '110px',
              minWidth: '110px',
              padding: '0.45rem 0.6rem',
              background: 'rgba(16, 185, 129, 0.08)',
              borderRight: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '2px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', color: '#10b981', fontSize: '0.72rem', fontWeight: 700 }}>
              <Mic size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span style={{ wordBreak: 'break-word' }} title={currentPerformer || 'User Dub'}>
                {currentPerformer && currentPerformer.trim() && currentPerformer !== 'Artist' ? currentPerformer : 'User Dub'}
              </span>
            </div>
          </div>

          {/* Lane with user take blocks */}
          <div style={{ flex: 1, height: '48px', position: 'relative', background: 'rgba(15, 23, 42, 0.55)', overflow: 'hidden' }}>
            {/* Grid ticks */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.1 }}>
              {ticks.map((s) => (
                <div key={s} style={{ position: 'absolute', top: 0, bottom: 0, borderLeft: '1px solid #fff', left: `${(s / safeDuration) * 100}%` }} />
              ))}
            </div>

            {/* Unloaded State */}
            {dialogueLines.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'rgba(148, 163, 184, 0.5)',
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                  userSelect: 'none'
                }}
              >
                SoulLess — Select a Soul from SoulVault
              </div>
            )}

            {/* Render take blocks for lines */}
            {dialogueLines.map((line, idx) => {
              const lineStart = line.start ?? line.startTime ?? 0;
              const lineEnd = line.end ?? line.endTime ?? safeDuration;
              const visLineStart = Math.max(0, lineStart);
              const visLineEnd = Math.min(safeDuration, lineEnd);
              const leftPct = (visLineStart / safeDuration) * 100;
              const widthPct = Math.max(2, ((visLineEnd - visLineStart) / safeDuration) * 100);

              // Find takes matching this specific line:
              // 1. Explicit line ID match
              // 2. Timecode overlap fallback (+/- 0.15s tolerance)
              const lineTakes = takes.filter((t) => {
                if (t.lineIds && t.lineIds.length > 0) {
                  return Boolean(line.id && t.lineIds.includes(line.id));
                }
                if (t.lineStart !== undefined && t.lineEnd !== undefined) {
                  const overlapStart = Math.max(t.lineStart, lineStart);
                  const overlapEnd = Math.min(t.lineEnd, lineEnd);
                  return (overlapEnd - overlapStart) > Math.min(0.2, (lineEnd - lineStart) * 0.4);
                }
                return false;
              });

              const isDubSelected = Boolean(line.id && selectedLineIds && selectedLineIds.has(line.id));
              const isInspected = selectedLineId === line.id;

              if (lineTakes.length === 0) {
                // Empty state outline: OFF (dormant) by default at scene open, armed (RED) when selected in teleprompter
                return (
                  <div
                    key={`user-empty-${line.id || idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectLine(line.id, 'user');
                      onSeek(visLineStart);
                    }}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      bottom: '6px',
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      borderRadius: '4px',
                      border: isDubSelected
                        ? (isInspected ? '2px solid #ffffff' : '1.5px solid #ef4444')
                        : (isInspected ? '1.5px dashed rgba(168, 85, 247, 0.8)' : '1px dashed rgba(255, 255, 255, 0.18)'),
                      background: isDubSelected
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.42) 0%, rgba(185, 28, 28, 0.35) 100%)'
                        : (isInspected ? 'rgba(139, 92, 246, 0.12)' : 'rgba(15, 23, 42, 0.6)'),
                      boxShadow: isDubSelected
                        ? (isInspected ? '0 0 18px rgba(239, 68, 68, 0.85)' : '0 0 12px rgba(239, 68, 68, 0.55)')
                        : (isInspected ? '0 0 8px rgba(168, 85, 247, 0.3)' : 'none'),
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: isDubSelected ? 700 : 600,
                      color: isDubSelected ? '#fee2e2' : 'rgba(148, 163, 184, 0.75)',
                      padding: '0 4px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease'
                    }}
                    title={`Line #${idx + 1}: ${isDubSelected ? 'Armed for Recording (Red)' : 'Dormant slot'}`}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%', padding: '0 4px' }}>
                      <span style={{ color: isDubSelected ? '#ef4444' : 'rgba(255,255,255,0.4)', fontSize: '10px', flexShrink: 0 }}>
                        {isDubSelected ? '●' : '○'}
                      </span>
                      {isDubSelected && (
                        <span style={{
                          background: '#ef4444',
                          color: '#ffffff',
                          fontSize: '8px',
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}>
                          DUB
                        </span>
                      )}
                      <span style={{ fontWeight: 700, fontSize: '0.84rem', color: isDubSelected ? '#ffffff' : 'rgba(226, 232, 240, 0.65)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {line.text || line.originalText || `Line #${idx + 1}`}
                      </span>
                    </span>
                  </div>
                );
              }

              // Pick active take, sorted consistently with SelectedLineInspector (newest/highest take number first)
              const sortedLineTakes = [...lineTakes].sort(
                (a, b) => (b.takeNumber || 0) - (a.takeNumber || 0) || (b.createdAt || 0) - (a.createdAt || 0) || b.id.localeCompare(a.id)
              );
              const dedicatedTake = sortedLineTakes.find(
                (t) => t.isActive && t.lineIds && t.lineIds.includes(line.id)
              );
              const activeTake = dedicatedTake || sortedLineTakes.find((t) => t.isActive) || sortedLineTakes[0];

              const rawStart = activeTake.lineStart !== undefined ? activeTake.lineStart : lineStart;
              const rawEnd = activeTake.lineEnd !== undefined ? activeTake.lineEnd : lineEnd;
              const takeStart = rawStart;
              const takeEnd = rawEnd;

              // Clean clipping for takes slipped before time 0
              const isClippedBeforeZero = takeStart < 0;
              const visibleTakeStart = Math.max(0, takeStart);
              const visibleTakeEnd = Math.min(safeDuration, takeEnd);
              const visibleTakeDuration = Math.max(0.05, visibleTakeEnd - visibleTakeStart);
              const takeLeftPct = (visibleTakeStart / safeDuration) * 100;
              const takeWidthPct = Math.max(2, (visibleTakeDuration / safeDuration) * 100);
              const isTakeReplacing = activeTake.isActive !== false;
              const isPartnerTake = Boolean(
                activeTake.isLocked ||
                activeTake.isPartnerTake ||
                (activeTake.performerName && currentPerformer && activeTake.performerName.toLowerCase() !== currentPerformer.toLowerCase())
              );

              return (
                <div
                  key={`user-take-${activeTake.id || idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectLine(line.id, 'user');
                    onSeek(visibleTakeStart);
                  }}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    bottom: '4px',
                    left: `${takeLeftPct}%`,
                    width: `${takeWidthPct}%`,
                    borderRadius: isClippedBeforeZero ? '0 4px 4px 0' : '4px',
                    opacity: isTakeReplacing ? 1 : 0.85,
                    background: isDubSelected
                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)'
                      : (isPartnerTake
                          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)'
                          : (isTakeReplacing
                              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.65) 0%, rgba(5, 150, 105, 0.7) 100%)')),
                    border: isInspected
                      ? '2px solid #ffffff'
                      : isDubSelected
                        ? '1.5px solid #ef4444'
                        : (isPartnerTake
                            ? '1.5px solid #fbbf24'
                            : (isTakeReplacing
                                ? '1.5px solid #34d399'
                                : '1px solid rgba(16, 185, 129, 0.6)')),
                    borderLeft: isClippedBeforeZero
                      ? '3px solid #f59e0b'
                      : (isInspected
                          ? '2px solid #ffffff'
                          : isDubSelected
                            ? '1.5px solid #ef4444'
                            : (isPartnerTake
                                ? '1.5px solid #fbbf24'
                                : (isTakeReplacing
                                    ? '1.5px solid #34d399'
                                    : '1px solid rgba(16, 185, 129, 0.6)'))),
                    boxShadow: isInspected
                      ? (isDubSelected
                          ? '0 0 18px rgba(239, 68, 68, 0.85)'
                          : (isPartnerTake
                              ? '0 0 18px rgba(245, 158, 11, 0.9)'
                              : '0 0 16px rgba(16, 185, 129, 0.8)'))
                      : isDubSelected
                        ? '0 0 14px rgba(239, 68, 68, 0.6)'
                        : (isPartnerTake
                            ? '0 0 12px rgba(245, 158, 11, 0.5)'
                            : (isTakeReplacing
                                ? '0 0 12px rgba(16, 185, 129, 0.45)'
                                : '0 2px 6px rgba(0, 0, 0, 0.3)')),
                    color: '#ffffff',
                    padding: isInspected ? '0 10px' : '0 6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '4px',
                    cursor: isPartnerTake ? 'default' : 'pointer',
                    fontSize: '10px',
                    fontWeight: 700,
                    zIndex: isInspected ? 25 : 10,
                    transition: 'border 0.15s ease, background 0.15s ease, box-shadow 0.15s ease'
                  }}
                  title={isPartnerTake
                    ? `Locked Duet Take by @${activeTake.performerName || 'Partner'} (${takeStart.toFixed(2)}s - ${takeEnd.toFixed(2)}s)`
                    : `Take ${activeTake.takeNumber || 1} (${takeStart.toFixed(2)}s - ${takeEnd.toFixed(2)}s${isClippedBeforeZero ? ' • clipped before 0.00s' : ''}) — ${isDubSelected ? 'ARMED FOR RECORDING (RED)' : (isTakeReplacing ? 'Active replacement take (Green)' : 'Inactive take')}`}
                >
                  {/* Left In-Point Handle for Take (Disabled for Partner Takes) */}
                  {!isPartnerTake && (
                    <div
                      data-role="handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDraggingHandle({ takeId: activeTake.id, type: 'start' });
                      }}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        cursor: 'ew-resize',
                        background: isInspected ? '#ffffff' : (isDubSelected ? '#f87171' : (isClippedBeforeZero ? '#fbbf24' : 'rgba(255, 255, 255, 0.85)')),
                        borderTopLeftRadius: isClippedBeforeZero ? '0px' : '3px',
                        borderBottomLeftRadius: isClippedBeforeZero ? '0px' : '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30,
                        boxShadow: '1px 0 3px rgba(0,0,0,0.4)'
                      }}
                      title={isClippedBeforeZero ? `In-Point: ${takeStart.toFixed(2)}s (clipped before 0.00s)` : "Drag to adjust Take In-Point"}
                    >
                      <div style={{ width: '2px', height: '14px', background: isClippedBeforeZero ? '#b45309' : (isDubSelected ? '#991b1b' : '#059669'), borderRadius: '1px' }} />
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const isAuditioningThis = auditioningTakeId === activeTake.id;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onAuditionTake) onAuditionTake(activeTake);
                          }}
                          style={{ background: 'transparent', border: 'none', color: isAuditioningThis ? '#fcd34d' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                          title={isAuditioningThis ? "Stop Audition" : "Audition Take"}
                        >
                          {isAuditioningThis ? <Pause size={10} /> : <Play size={10} fill="currentColor" />}
                        </button>
                      );
                    })()}
                    
                    {isPartnerTake ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#fffbeb', fontWeight: 800, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        <Lock size={10} style={{ color: '#fef3c7', flexShrink: 0 }} />
                        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>@{activeTake.performerName || 'Partner'}</span>
                      </span>
                    ) : (
                      <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', fontWeight: 700 }}>
                        Take {activeTake.takeNumber || 1}
                      </span>
                    )}

                    {isPartnerTake && (
                      <span
                        style={{
                          background: 'rgba(0, 0, 0, 0.4)',
                          color: '#fef3c7',
                          fontSize: '8px',
                          fontWeight: 800,
                          padding: '1px 4px',
                          borderRadius: '3px',
                          border: '1px solid rgba(251, 191, 36, 0.4)',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}
                      >
                        DUET
                      </span>
                    )}

                    {isDubSelected && !isPartnerTake && (
                      <span
                        style={{
                          background: 'rgba(0, 0, 0, 0.35)',
                          color: '#fee2e2',
                          fontSize: '8px',
                          fontWeight: 800,
                          padding: '1px 4px',
                          borderRadius: '3px',
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}
                      >
                        ARMED
                      </span>
                    )}
                  </div>

                  {/* Clean Slip Amount Value */}
                  {(() => {
                    const slipDelta = Math.round((takeStart - lineStart) * 100) / 100;
                    const slipLabel = slipDelta === 0 ? '0.00s' : slipDelta > 0 ? `+${slipDelta.toFixed(2)}s` : `${slipDelta.toFixed(2)}s`;
                    return (
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '9px',
                          fontWeight: 700,
                          color: isPartnerTake
                            ? '#fde68a'
                            : (isClippedBeforeZero ? '#fcd34d' : (Math.abs(slipDelta) > 0.001 ? '#6ee7b7' : 'rgba(255, 255, 255, 0.75)')),
                          background: 'rgba(0, 0, 0, 0.35)',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          border: isPartnerTake
                            ? '1px solid rgba(245, 158, 11, 0.4)'
                            : (isClippedBeforeZero ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(255, 255, 255, 0.12)'),
                          whiteSpace: 'nowrap'
                        }}
                        title={`Slip offset: ${slipLabel} relative to dialogue line${isClippedBeforeZero ? ' (clipped before 0.00s)' : ''}`}
                      >
                        {slipLabel}
                      </span>
                    );
                  })()}

                  {/* Right Out-Point Handle for Take (Disabled for Partner Takes) */}
                  {!isPartnerTake && (
                    <div
                      data-role="handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDraggingHandle({ takeId: activeTake.id, type: 'end' });
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        cursor: 'ew-resize',
                        background: isInspected ? '#ffffff' : (isDubSelected ? '#f87171' : 'rgba(255, 255, 255, 0.85)'),
                        borderTopRightRadius: '3px',
                        borderBottomRightRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30,
                        boxShadow: '-1px 0 3px rgba(0,0,0,0.4)'
                      }}
                      title="Drag to adjust Take Out-Point"
                    >
                      <div style={{ width: '2px', height: '14px', background: isDubSelected ? '#991b1b' : '#059669', borderRadius: '1px' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── TIME RULER / SECONDS TICK BAR ── */}
        <div style={{ display: 'flex', alignItems: 'center', height: '18px', background: '#070913', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
          <div style={{ width: '110px', minWidth: '110px', borderRight: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '9px', color: 'var(--text-muted)', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
            TIMECODE
          </div>
          <div style={{ flex: 1, position: 'relative', height: '100%' }}>
            {ticks.map((s) => (
              <span
                key={s}
                style={{
                  position: 'absolute',
                  left: `${(s / safeDuration) * 100}%`,
                  transform: 'translateX(-50%)',
                  fontSize: '9px',
                  fontFamily: 'var(--font-mono)',
                  color: 'rgba(255, 255, 255, 0.35)',
                  lineHeight: '18px'
                }}
              >
                {s}s
              </span>
            ))}
          </div>
        </div>

        {/* ── LAYER 2 (BOTTOM TRACK): ORIGINAL VOICES / LINES ── */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Track Header Label */}
          <div
            style={{
              width: '110px',
              minWidth: '110px',
              padding: '0.45rem 0.6rem',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRight: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '2px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#60a5fa', fontSize: '0.72rem', fontWeight: 700 }}>
              <Volume2 size={13} />
              <span>Pure Soul</span>
            </div>
          </div>

          {/* Lane with original dialogue line blocks */}
          <div style={{ flex: 1, height: '52px', position: 'relative', background: 'rgba(10, 15, 30, 0.85)', overflow: 'hidden' }}>
            {/* Grid ticks */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.1 }}>
              {ticks.map((s) => (
                <div key={s} style={{ position: 'absolute', top: 0, bottom: 0, borderLeft: '1px solid #fff', left: `${(s / safeDuration) * 100}%` }} />
              ))}
            </div>

            {/* Unloaded State */}
            {dialogueLines.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'rgba(148, 163, 184, 0.4)',
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                  userSelect: 'none'
                }}
              >
                No Auras loaded
              </div>
            )}

            {/* Render dialogue line segments */}
            {dialogueLines.map((line, idx) => {
              const start = line.start ?? line.startTime ?? 0;
              const end = line.end ?? line.endTime ?? safeDuration;
              const visStart = Math.max(0, start);
              const visEnd = Math.min(safeDuration, end);
              const leftPct = (visStart / safeDuration) * 100;
              const widthPct = Math.max(2, ((visEnd - visStart) / safeDuration) * 100);
              const isDubSelected = Boolean(line.id && selectedLineIds && selectedLineIds.has(line.id));
              const isInspected = selectedLineId === line.id;
              const { status } = getLineStatus(line.id, takes, currentPerformer);

              // Orig Video track blocks are ALWAYS "ON" - never grayed out!
              // When armed for replacement: vivid red gradient with [ARMED] badge
              // When standard guide: vivid illuminated studio blue gradient
              const bg = isDubSelected
                ? 'linear-gradient(180deg, rgba(220, 38, 38, 0.92) 0%, rgba(127, 29, 29, 0.96) 100%)'
                : 'linear-gradient(180deg, rgba(30, 58, 138, 0.88) 0%, rgba(15, 23, 42, 0.96) 100%)';

              const border = isInspected
                ? '2px solid #ffffff'
                : isDubSelected
                  ? '1.5px solid #ef4444'
                  : '1px solid rgba(96, 165, 250, 0.65)';

              const boxShadow = isInspected
                ? (isDubSelected ? '0 0 18px rgba(239, 68, 68, 0.85)' : '0 0 16px rgba(255, 255, 255, 0.6)')
                : isDubSelected
                  ? '0 0 14px rgba(239, 68, 68, 0.6)'
                  : '0 2px 8px rgba(0, 0, 0, 0.4)';

              return (
                <div
                  key={line.id || idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectLine(line.id, 'orig');
                    onSeek(visStart);
                  }}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    bottom: '4px',
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    borderRadius: '4px',
                    background: bg,
                    border,
                    boxShadow,
                    color: '#ffffff',
                    opacity: 1, // ALWAYS "ON" - Never grayed out!
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 6px',
                    cursor: 'pointer',
                    zIndex: isInspected ? 20 : 10,
                    transition: 'border 0.15s ease, background 0.15s ease, box-shadow 0.15s ease'
                  }}
                  title={`"${line.text || line.originalText || `Line #${idx + 1}`}" (${start.toFixed(1)}s - ${end.toFixed(1)}s) — ${isDubSelected ? 'ARMED FOR DUBBING (RED)' : 'Orig Video Guide (Blue)'}`}
                >
                  {/* Left In-Point Handle */}
                  {isInspected && (
                    <div
                      data-role="handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDraggingHandle({ lineId: line.id, type: 'start' });
                      }}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        cursor: 'ew-resize',
                        background: '#ffffff',
                        borderTopLeftRadius: '3px',
                        borderBottomLeftRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30
                      }}
                      title="Drag to fine-tune Start Time"
                    >
                      <div style={{ width: '2px', height: '14px', background: '#334155' }} />
                    </div>
                  )}

                  {/* Line dialogue text in bold without character names */}
                  <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none', padding: isInspected ? '0 8px' : '0 2px', flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isDubSelected && (
                      <span
                        style={{
                          background: 'rgba(0, 0, 0, 0.4)',
                          color: '#fee2e2',
                          fontSize: '8px',
                          fontWeight: 800,
                          padding: '1px 5px',
                          borderRadius: '3px',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}
                      >
                        ARMED
                      </span>
                    )}
                    <span style={{ fontWeight: 800, fontSize: '0.86rem', color: '#ffffff', letterSpacing: '0.01em', textShadow: '0 1px 3px rgba(0,0,0,0.8)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {line.text || line.originalText || `Line #${idx + 1}`}
                    </span>
                  </div>


                  {/* Right Out-Point Handle */}
                  {isInspected && (
                    <div
                      data-role="handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDraggingHandle({ lineId: line.id, type: 'end' });
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        cursor: 'ew-resize',
                        background: '#ffffff',
                        borderTopRightRadius: '3px',
                        borderBottomRightRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30
                      }}
                      title="Drag to fine-tune End Time"
                    >
                      <div style={{ width: '2px', height: '14px', background: '#334155' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend & Help tip */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1rem', background: 'rgba(255, 255, 255, 0.02)', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap', gap: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'linear-gradient(180deg, #1e3a8a 0%, #0f172a 100%)', border: '1px solid rgba(96, 165, 250, 0.7)', display: 'inline-block' }} />
              Pure Soul Guide
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', border: '1px solid #ef4444', display: 'inline-block' }} />
              Armed to Record
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#10b981', display: 'inline-block' }} />
              Recorded Take
            </span>
          </div>
      </div>
    </div>
  );
};
